import React, { useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Select, Switch, Tooltip } from 'antd';
import {
  GlobalOutlined,
  SearchOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { data as allRoutes } from 'configs/menu-config';
import { STOREFRONT_ORIGIN } from 'configs/app-global';
import {
  PERIOD_PRESETS,
  setCustomRange,
  setPreset,
  toggleCompare,
} from 'redux/slices/period';
import CommandPalette from './command-palette';

const { RangePicker } = DatePicker;

const PRESET_LABEL_KEYS = {
  subWeek: 'this.week',
  subMonth: 'this.month',
  subYear: 'this.year',
};

/**
 * Walks the menu tree to find where the current path lives, so the breadcrumb
 * reads from the same source as the sidebar and the tab bar and cannot drift
 * out of sync with either.
 */
function findBreadcrumb(menuList, pathname) {
  const path = pathname.replace(/^\/+/, '').split('?')[0];
  if (!path) return [];

  let best = null;
  const consider = (trail, url) => {
    if (!url) return;
    const segment = String(url).replace(/^\/+/, '');
    if (path !== segment && !path.startsWith(`${segment}/`)) return;
    if (!best || segment.length > best.length) {
      best = { trail, length: segment.length };
    }
  };

  menuList?.forEach((item) => {
    consider([item.name], item.url);
    item.submenu?.forEach((sub) => {
      consider([item.name, sub.name], sub.url);
      sub.children?.forEach((child) => {
        consider([item.name, sub.name, child.name], child.url);
      });
    });
  });

  return best?.trail ?? [];
}

export default function AppHeader() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const [paletteOpen, setPaletteOpen] = useState(false);

  const { user } = useSelector((state) => state.auth, shallowEqual);
  const period = useSelector((state) => state.period, shallowEqual);

  // Memoised so the fallback chain doesn't produce a fresh array each render
  // and re-run the breadcrumb and palette memos below it.
  const menuList = useMemo(
    () => user?.urls ?? allRoutes[user?.role] ?? [],
    [user],
  );
  const trail = useMemo(
    () => findBreadcrumb(menuList, pathname),
    [menuList, pathname],
  );

  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Pages without a period hide the range controls rather than showing a
  // control that changes nothing.
  const supportsPeriod = useMemo(() => {
    const path = pathname.replace(/^\/+/, '');
    return path === 'dashboard' || path.startsWith('report');
  }, [pathname]);

  const rangeValue =
    period?.preset === 'custom' && period?.startDate && period?.endDate
      ? [moment(period.startDate), moment(period.endDate)]
      : null;

  return (
    <>
      <div className='ks-appbar'>
        <nav className='ks-crumbs' aria-label={t('breadcrumb')}>
          {trail.length ? (
            trail.map((name, index) => (
              <span className='ks-crumbs__item' key={`${name}-${index}`}>
                {index > 0 && <span className='ks-crumbs__sep'>/</span>}
                <span
                  className={
                    index === trail.length - 1 ? 'ks-crumbs__current' : undefined
                  }
                >
                  {t(name)}
                </span>
              </span>
            ))
          ) : (
            <span className='ks-crumbs__current'>{t('dashboard')}</span>
          )}
        </nav>

        <div className='ks-appbar__tools'>
          {supportsPeriod && (
            <>
              <Select
                className='ks-appbar__preset'
                size='small'
                value={period?.preset === 'custom' ? 'custom' : period?.preset}
                onChange={(value) => {
                  if (value !== 'custom') dispatch(setPreset(value));
                }}
                aria-label={t('period')}
              >
                {PERIOD_PRESETS.map((preset) => (
                  <Select.Option key={preset} value={preset}>
                    {t(PRESET_LABEL_KEYS[preset])}
                  </Select.Option>
                ))}
                <Select.Option value='custom' disabled>
                  {t('custom.range')}
                </Select.Option>
              </Select>

              <RangePicker
                className='ks-appbar__range'
                size='small'
                allowClear
                value={rangeValue}
                suffixIcon={<CalendarOutlined />}
                onChange={(dates) => {
                  if (!dates || !dates[0] || !dates[1]) {
                    dispatch(setPreset('subMonth'));
                    return;
                  }
                  dispatch(
                    setCustomRange({
                      startDate: dates[0].startOf('day').toISOString(),
                      endDate: dates[1].endOf('day').toISOString(),
                    }),
                  );
                }}
              />

              <label className='ks-appbar__compare'>
                <Switch
                  size='small'
                  checked={!!period?.compare}
                  onChange={(checked) => dispatch(toggleCompare(checked))}
                />
                <span>{t('compare.to.previous.period')}</span>
              </label>
            </>
          )}

          <Tooltip title={`${t('search')} (Ctrl+K)`}>
            <Button
              className='ks-appbar__icon-btn'
              icon={<SearchOutlined />}
              onClick={() => setPaletteOpen(true)}
              aria-label={t('search')}
            />
          </Tooltip>

          {/*
            A way to look at the shop, labelled as one.

            This was a question mark captioned "help" that opened the storefront
            — so the one button an admin might press for help took them to the
            shop front page, and the thing they actually do want, a look at what
            they have just published, was not offered anywhere. The address comes
            from the configured origin rather than a hardcoded domain, so it
            follows a staging site or a local dev server.
          */}
          <Tooltip title={t('preview.site', 'Preview site')}>
            <Button
              className='ks-appbar__preview-btn'
              icon={<GlobalOutlined />}
              href={STOREFRONT_ORIGIN}
              target='_blank'
              rel='noreferrer'
            >
              {t('preview.site', 'Preview site')}
            </Button>
          </Tooltip>
        </div>
      </div>

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        menuList={menuList}
      />
    </>
  );
}
